import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { ToastrService } from '@shared/services/toastr.service';
import _ from 'lodash';
import { Site } from '@shared/models/site';
import {
  Certificate,
  Csr,
  OrganizationSiteService,
  PassTlsClientCerts,
} from '@shared/services/organization-site.service';
import { TlsOptions } from '@shared/services/organization-tls-options.service';
import moment from 'moment/moment';
import { map } from 'rxjs/operators';
import { catchError, of } from 'rxjs';
import { ConfirmService } from '@shared/services/confirm.service';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgFor, NgIf } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DownloadIconComponent } from '@shared/components/download-icon/download-icon.component';
import { LabelComponent } from '@shared/components/label/label.component';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { FormsModule } from '@angular/forms';
import { MtlsComponent } from './mtls/mtls.component';
import { ModalBasicComponent } from '@shared/components/modal-basic/modal-basic.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { DateFormatPipe } from '@shared/pipe/filter.pipe';

@Component({
  selector: 'app-certificates',
  templateUrl: './certificates.component.html',
  styleUrls: ['./certificates.component.scss', '../add-site.component.scss'],
  imports: [
    HelpComponent,
    NgFor,
    NgIf,
    NgbTooltip,
    DownloadIconComponent,
    LabelComponent,
    SwitchComponent,
    FormsModule,
    MtlsComponent,
    ModalBasicComponent,
    NgSelectComponent,
    DateFormatPipe,
    TranslatePipe,
  ],
})
export class CertificatesComponent implements OnInit {
  @Input({ required: true }) site: Site;

  @Output() isACertificateInError = new EventEmitter<boolean>();

  @ViewChild('clearCert') clearCert: ElementRef;
  @ViewChild('clearChain') clearChain: ElementRef;
  @ViewChild('loadPkcs12') loadPkcs12: any;
  @ViewChild('pkcs12') modalPkcs12: any;
  @ViewChild('modalNewCsr') modalNewCsr: any;
  @ViewChild('modalAddCchain') modalAddCchain: any;

  certificates: CertificateVM[] = [];
  tlsOptions: TlsOptions[];
  p12Password: string;
  csr: Csr;
  crt: any = {};
  p12File: File = null;
  makeCertificateActive = true;
  showLetsEncryptFallback = false;

  constructor(
    private siteApi: OrganizationSiteService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private confirmService: ConfirmService,
  ) {}

  ngOnInit(): void {
    if (this.site.domainName) {
      this.getCertificates();
      if (!this.site.cdn) this.loadTlsOptions();
      this.csr = { commonName: this.site.domainName };
    }
  }
  activateCert(certificate: CertificateVM) {
    if (moment().isAfter(moment(certificate.expiredAt))) {
      this.toastr.error('CertificatExpire');
      return;
    }
    this.siteApi.updateCertificate(this.site.domainName, certificate.id, { active: true }).subscribe({
      next: (res) => {
        this.toastr.success('ActivationCertificatSucces');
        this.getCertificates();
      },
      error: (err) => {
        this.toastr.error('ActivationCertificatEchouee');
      },
    });
  }

  createFullChain(certificateId: number, chain: any) {
    this.siteApi.updateCertificate(this.site.domainName, certificateId, { fullChainCert: chain }).subscribe({
      next: (res) => {
        this.getCertificates();
        this.crt = {};
        this.modalAddCchain.hide();
        this.toastr.success('AjoutCertificatSucces');
        this.clearInputCerts();
        this.modalAddCchain.hide();
      },
      error: (err) => {
        //this.toastr.error(err.status.message);
        this.crt = {};
        this.clearInputCerts();
        this.modalAddCchain.hide();
      },
    });
  }

  getCertificates() {
    this.certificates = [];
    this.siteApi.getCertificates(this.site.domainName).subscribe((certificates) => {
      this.certificates = _.map(certificates, (cert) => ({
        ...cert,
        hasCsr: cert.csr ? true : false,
        hasCert: cert.fullChainCert ? true : false,
        stepNbr: this.computeStepNbr(cert),
        expanded: false,
      }));
      this.isACertificateInError.emit(this.certificates.some((c) => c.error));
      this.showLetsEncryptFallback = !!this.certificates.find((c) => c.active && c.type == 'CE');
    });
  }

  computeStepNbr(cert: Certificate) {
    let stepNbr = 0;
    stepNbr = cert.fullChainCert ? ++stepNbr : stepNbr;
    stepNbr = cert.active ? ++stepNbr : stepNbr;
    return stepNbr;
  }

  handlePkcs12(files: FileList) {
    this.p12File = files.item(0) as File;
  }

  showModalAddChain(h: any) {
    this.clearInputCerts();
    this.modalAddCchain.show();
    this.crt = h;
  }

  confirmDeleteCert(p: any) {
    this.confirmService.confirm({ html: this.translate.instant('VoulezVousSupprimerCeCertificat') }, () =>
      this.deleteCert(p.id),
    );
  }

  deleteCert(id: number) {
    this.siteApi.deleteCertificate(this.site.domainName, id).subscribe(() => {
      this.getCertificates();
    });
  }

  createCsr() {
    this.siteApi.addCsr(this.site.domainName, this.csr).subscribe((certificate) => {
      this.modalNewCsr.hide();
      this.csr = { commonName: this.site.domainName };
      this.downloadFile(certificate.csr, `${this.site.domainName}.csr`);
      this.getCertificates();
    });
  }

  showModalPkcs12() {
    this.modalPkcs12.show();
  }

  handleKeyEnterP12(event) {
    event.preventDefault();
    this.saveP12Certif();
  }

  saveP12Certif() {
    this.siteApi
      .uploadP12(this.site.domainName, {
        p12Data: this.p12File,
        p12Password: this.p12Password,
        makeActive: this.makeCertificateActive,
      })
      .subscribe({
        next: (res) => {
          this.p12Password = null;
          this.modalPkcs12.hide();
          this.loadPkcs12.nativeElement.value = '';
          this.p12File = null;
          this.getCertificates();
          this.toastr.success(['CertificatAjoutSucces', this.site]);
        },
      });
  }

  confirmCreateCertCertChain() {
    const fullChain = `${this.crt.cert || ''}${this.crt.chain ? `\n${this.crt.chain}` : ''}`;
    if (fullChain === '') {
      return;
    }
    this.createFullChain(this.crt.id, fullChain);
  }

  clearInputCerts() {
    this.clearCert.nativeElement.value = '';
    this.crt.certName = '';
    this.clearChain.nativeElement.value = '';
    this.crt.chainName = '';
  }

  handleCert(files: FileList) {
    this.crt.certName = files.item(0).name;
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      this.crt.cert = fileReader.result;
    };
    fileReader.readAsText(files.item(0));
  }

  handleChain(files: FileList) {
    this.crt.chainName = files.item(0).name;
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      this.crt.chain = fileReader.result;
    };
    fileReader.readAsText(files.item(0));
  }

  protected addSanFromSearchTerm(select: NgSelectComponent) {
    const term = select.searchTerm?.trim();
    if (!term) {
      return;
    }
    this.csr.san = this.csr.san ?? [];
    if (!this.csr.san.includes(term)) {
      this.csr.san = [...this.csr.san, term];
    }
    select.searchTerm = null;
  }

  downloadFile(str: string, fileName: string) {
    if (str == null || str === '') {
      this.toastr.info('ElementTelechargeVide');
      return;
    }
    const blob = new Blob([str], {
      type: 'text/plain;charset=utf-8',
    });
    saveAs(blob, fileName);
  }

  protected readonly PassTlsClientCerts = PassTlsClientCerts;

  private loadTlsOptions() {
    this.siteApi
      .getAvailableTlsOptions(this.site.domainName, { number: 0, size: 100 })
      .subscribe((tlsOptions) => (this.tlsOptions = tlsOptions.content));
  }

  protected toggleLetsEncryptCertificateFallbackEnabled = (e) => {
    return this.siteApi
      .patchSite(this.site.domainName, {
        letsEncryptCertificateFallbackEnabled: !this.site.letsEncryptCertificateFallbackEnabled,
      })
      .pipe(
        map((site) => {
          this.toastr.success();
          this.site.letsEncryptCertificateFallbackEnabled = site.letsEncryptCertificateFallbackEnabled;
          return true;
        }),
        catchError((err) => of(false)),
      );
  };

  protected onPasteSan(event: ClipboardEvent) {
    const clipData = event.clipboardData.getData('text/plain');

    if (clipData != null) {
      event.preventDefault();
      event.stopPropagation();
      const values = clipData
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter((s) => s != '');
      this.csr.san = [...this.csr.san, ...values];
    }
  }
}

type CertificateVM = Certificate & {
  hasCsr: boolean;
  hasCert: boolean;
  expanded: boolean;
  stepNbr: number;
};
